import React, { useCallback } from 'react';

import type { DocParts } from '@aline/common';
import { saveAs } from 'file-saver';
import { useSnackbar } from 'notistack';
import sanitize from 'sanitize-filename';
import { LoadingSnackbar } from 'src/components';
import { isDev, isStaging } from 'src/config';
import { useSelectCurrentUser } from 'src/gql';
import { setFileExtension } from 'src/helpers/utils';
import { v4 as uuid4 } from 'uuid';

declare global {
  interface Window {
    /** Set to `true` from the dev console to reveal JSON export when impersonating a non-support user. */
    _alineAllowJSONExport?: boolean;
  }
}

export function useIsJsonDownloadAvailable(): boolean {
  const currentUser = useSelectCurrentUser();
  return isDev || isStaging || !!currentUser?.supportRole || !!window._alineAllowJSONExport;
}

export function useDownloadJSON() {
  const downloadJSON = useCallback(
    async (_documentId: UUID, options: { docParts: DocParts; name: string }) => {
      const { docParts, name } = options;

      const jsonString = JSON.stringify(docParts, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });

      saveAs(blob, sanitize(setFileExtension(name, 'json')));
    },
    []
  );

  return { downloadJSON };
}

export function useDownloadJSONWithLoading() {
  const { downloadJSON } = useDownloadJSON();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();

  const downloadJSONWithLoading = useCallback(
    async (...args: Parameters<typeof downloadJSON>) => {
      const loadSnackKey = uuid4();
      enqueueSnackbar('', {
        content: <LoadingSnackbar id={loadSnackKey} message="Downloading JSON..." />,
        key: loadSnackKey,
        persist: true,
      });
      const res = await downloadJSON(...args);
      closeSnackbar(loadSnackKey);
      return res;
    },
    [closeSnackbar, downloadJSON, enqueueSnackbar]
  );

  return { downloadJSONWithLoading };
}
